import type React from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/api/useApi";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const api = useApi();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    const result = await api.post("access/forgotpassword", {
      username: email.trim(),
    });

    if (result.statusCode === "00") {
      setSuccess(
        "Password reset instructions have been sent to your email address"
      );

      localStorage.setItem("username", email.trim());

      setTimeout(() => {
        navigate("/reset-password");
      }, 3000);
    } else {
      setError(
        result.message || "Failed to send reset instructions. Please try again."
      );
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <section className="flex w-full items-center justify-center md:p-8 lg:p-16">
      <motion.div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg sm:p-8"
        initial={{ x: 100, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, opacity: 1, scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: 0.1,
        }}
      >
        <div className="mb-6">
          <h1 className="text-xl font-medium text-gray-900">
            Forgot your password?
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Enter your email address and we'll send you instructions to reset
            your password.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-600">
            {success}
          </div>
        )}

        {api.error && !error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {api.error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-400 "
              placeholder="Enter your email address"
            />
          </div>

          <motion.button
            type="submit"
            disabled={api.isLoading || email.length === 0 || success.length > 0}
            className="w-full rounded-md bg-red-600 py-3 text-center font-medium text-white shadow-sm hover:bg-red-700  disabled:cursor-not-allowed disabled:opacity-70"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {api.isLoading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </span>
            ) : (
              "Send Reset Instructions"
            )}
          </motion.button>

          <motion.button
            type="button"
            onClick={handleBackToLogin}
            className="w-full rounded-md border border-gray-300 py-3 text-center font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Back to Login
          </motion.button>
        </form>
      </motion.div>
    </section>
  );
};

export default ForgotPassword;
